import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Plus, Minus, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ProgressBar from '../components/ProgressBar';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { sideImageMap, defaultSideMeta } from '../lib/productMeta';

const IncludedChipsCard = ({ side }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="mb-8"
  >
    <Card className="p-8 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
      <div className="flex items-center gap-6">
        <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
          <img src={side.image} alt={side.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Check className="w-6 h-6 text-primary" />
            <h3 className="text-2xl font-bold text-foreground">{side.name}</h3>
          </div>
          <p className="text-muted-foreground">{side.description}</p>
        </div>
        <div className="text-2xl font-bold text-primary">GRATIS</div>
      </div>
    </Card>
  </motion.div>
);

const QuantityControls = ({ quantity, onDecrease, onIncrease }) => (
  <div className="flex items-center justify-between bg-primary/10 rounded-lg p-2">
    <button
      onClick={onDecrease}
      className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
    >
      <Minus className="w-5 h-5" />
    </button>
    <span className="text-2xl font-bold text-foreground">{quantity}</span>
    <button
      onClick={onIncrease}
      className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
    >
      <Plus className="w-5 h-5" />
    </button>
  </div>
);

const SideItemCard = ({ extra, index, selected, quantity, onToggle, onQuantityChange }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1, duration: 0.4 }}
  >
    <Card
      onClick={() => !selected && onToggle(extra)}
      data-testid={`side-card-${extra.id}`}
      className={`product-card p-6 cursor-pointer border-2 bg-card hover:border-primary touch-feedback min-h-[240px] flex flex-col ${
        selected ? 'border-primary bg-primary/5' : 'border-border'
      }`}
    >
      <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
        {extra.image ? (
          <img src={extra.image} alt={extra.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-muted/20 flex items-center justify-center">
            <span className="text-3xl text-muted-foreground">{'\u2014'}</span>
          </div>
        )}
      </div>
      <div className="text-center flex-1">
        <h4 className="text-xl font-bold text-foreground mb-2">{extra.name}</h4>
        <p className="text-sm text-muted-foreground mb-3">{extra.description}</p>
        <p className="text-2xl font-bold text-primary">{'\u20AC'}{Number(extra.price).toFixed(2)}</p>
      </div>
      <div className="mt-4">
        {selected ? (
          <QuantityControls
            quantity={quantity}
            onDecrease={(e) => {
              e.stopPropagation();
              if (quantity === 1) onToggle(extra);
              else onQuantityChange(extra.id, -1);
            }}
            onIncrease={(e) => {
              e.stopPropagation();
              onQuantityChange(extra.id, 1);
            }}
          />
        ) : (
          <Button onClick={() => onToggle(extra)} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
            Toevoegen
          </Button>
        )}
      </div>
    </Card>
  </motion.div>
);

export const SideSelection = ({ order, setOrder }) => {
  const navigate = useNavigate();
  const [includedSide, setIncludedSide] = useState(null);
  const [optionalSides, setOptionalSides] = useState([]);
  const [extras, setExtras] = useState(order.extraSides || []);
  const [loading, setLoading] = useState(true);

  const fetchSides = useCallback(async () => {
    const { data, error } = await supabase
      .from('sides')
      .select('*')
      .order('is_included', { ascending: false });

    if (error) {
      setLoading(false);
      return;
    }

    const enriched = data.map(s => {
      const meta = sideImageMap[s.id] || defaultSideMeta;
      return { ...s, image: meta.image };
    });

    setIncludedSide(enriched.find(s => s.is_included));
    setOptionalSides(enriched.filter(s => !s.is_included));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSides();
  }, [fetchSides]);

  const handleToggleExtra = (extra) => {
    setExtras(prev => {
      const existing = prev.find(e => e.id === extra.id);
      if (existing) return prev.filter(e => e.id !== extra.id);
      return [...prev, { ...extra, quantity: 1 }];
    });
  };

  const handleQuantityChange = (extraId, delta) => {
    setExtras(prev => prev.map(e => {
      if (e.id === extraId) {
        const newQty = Math.max(1, Math.min(5, e.quantity + delta));
        return { ...e, quantity: newQty };
      }
      return e;
    }));
  };

  const isSelected = (extraId) => extras.some(e => e.id === extraId);
  const getQuantity = (extraId) => extras.find(e => e.id === extraId)?.quantity || 0;

  const handleContinue = () => {
    setOrder({ ...order, extraSides: extras });
    navigate('/review');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <ProgressBar currentStep={5} totalSteps={5} />
      <PageHeader title="Extras toevoegen" subtitle="Signature Chips zijn al inbegrepen!" />

      <div className="container max-w-6xl mx-auto px-6 mt-8">
        {includedSide && <IncludedChipsCard side={includedSide} />}

        <div className="mb-6">
          <h3 className="text-2xl font-semibold text-foreground mb-2">Optionele extras</h3>
          <p className="text-muted-foreground">Je kunt ook zonder extras doorgaan</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {optionalSides.map((extra, index) => (
            <SideItemCard
              key={extra.id}
              extra={extra}
              index={index}
              selected={isSelected(extra.id)}
              quantity={getQuantity(extra.id)}
              onToggle={handleToggleExtra}
              onQuantityChange={handleQuantityChange}
            />
          ))}
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex flex-col items-center gap-4">
          {extras.length === 0 && (
            <p className="text-muted-foreground text-sm">Je kunt ook zonder extra's doorgaan</p>
          )}
          <Button
            onClick={handleContinue}
            size="lg"
            data-testid="side-continue-btn"
            className="btn-kiosk bg-primary hover:bg-primary/90 text-primary-foreground px-16 py-8 text-xl font-semibold shadow-lg"
          >
            Ga verder naar overzicht
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default SideSelection;
